/**
 * Chip layer — replaces packages/amgbaccarat_v2/lib/presentation/flame/baccarat_flame_game.dart.
 *
 * Coordinate system: 1792×828 reference (PixiLayer's logical size).
 *
 * Bet-area scatter rectangles (baccarat_flame_game.dart:15-30,
 * ChipPosition(minX%, rangeX_px, minY%, rangeY_px)):
 *   268 Player Pair:  (0.394, 150, 0.386, 32)
 *   269 Banker Pair:  (0.51,  150, 0.386, 32)
 *   270 Perfect Pair: (0.394, 150, 0.465, 32)
 *   271 Either Pair:  (0.51,  150, 0.465, 32)
 *   265 Player:       (0.22,  200, 0.41,  290)
 *   267 Tie:          (0.39,  380, 0.57,  170)
 *   266 Banker:       (0.67,  200, 0.41,  290)
 *
 * Player origin (chips fly FROM): (size.x/2, size.y + 50) = (896, 878).
 * Dealer target   (chips fly TO on lose): (size.x*0.5, size.y*0.2) = (896, 166).
 * Player target   (chips fly TO on win):  (size.x*0.5, size.y*1.1) = (896, 911).
 *
 * Win/loss split (game_screen.dart:434-444):
 *   winMode 0 → halfHalf (push) — first half of chips → dealer, rest → player
 *   winMode 1 → moveAllToDealer (loss)
 *   winMode 2 → moveAllToPlayer (win)
 * Mixed-area wins (e.g. lost the main bet but won a pair side bet) split
 * per area — winning areas fly to player, losers fly to dealer.
 */
import { Container } from 'pixi.js';
import type { BetType } from '../types/game';
import { ChipSprite, preloadCoinTextures } from './chipSprite';
import { Easings, tween } from './tween';
import { REF_HEIGHT, REF_WIDTH } from '../config';

interface XY {
  x: number;
  y: number;
}

interface ScatterRect {
  minX: number;   // top-left x (px in 1792-space)
  rangeX: number; // scatter width (px)
  minY: number;
  rangeY: number;
}

/**
 * Convert ChipPosition(minXFrac, rangeXpx, minYFrac, rangeYpx) →
 * absolute pixel rect in 1792×828 space.
 */
function makeRect(
  minXFrac: number,
  rangeX: number,
  minYFrac: number,
  rangeY: number,
): ScatterRect {
  return {
    minX: minXFrac * REF_WIDTH,
    rangeX,
    minY: minYFrac * REF_HEIGHT,
    rangeY,
  };
}

const SCATTER: Record<BetType, ScatterRect> = {
  player:      makeRect(0.22,  200, 0.41,  290),
  banker:      makeRect(0.67,  200, 0.41,  290),
  tie:         makeRect(0.39,  380, 0.57,  170),
  playerPair:  makeRect(0.394, 150, 0.386, 32),
  bankerPair:  makeRect(0.51,  150, 0.386, 32),
  perfectPair: makeRect(0.394, 150, 0.465, 32),
  eitherPair:  makeRect(0.51,  150, 0.465, 32),
};

const PLAYER_ORIGIN: XY     = { x: REF_WIDTH / 2, y: REF_HEIGHT + 50 };
const DEALER_TARGET: XY     = { x: REF_WIDTH * 0.5, y: REF_HEIGHT * 0.2 };
const PLAYER_WIN_TARGET: XY = { x: REF_WIDTH * 0.5, y: REF_HEIGHT * 1.1 };

interface ActiveChip {
  bet: BetType;
  sprite: ChipSprite;
}

export class ChipLayer {
  private readonly root: Container;
  private active: ActiveChip[] = [];

  constructor(parent: Container) {
    this.root = new Container();
    this.root.label = 'ChipLayer';
    parent.addChild(this.root);
    void preloadCoinTextures();
  }

  /**
   * Add a chip at a random scatter point inside the bet area.
   * `chipIndex` is the index into GameConfig.chipValues — used for color
   * (chip image (chipIndex % 6) + 1), matching the picker's selected chip.
   */
  placeChip(bet: BetType, chipValue: number, chipIndex: number): void {
    const rect = SCATTER[bet];
    const target: XY = {
      x: rect.minX + Math.random() * rect.rangeX,
      y: rect.minY + Math.random() * rect.rangeY,
    };

    const sprite = new ChipSprite(chipValue, chipIndex);
    sprite.position.set(PLAYER_ORIGIN.x, PLAYER_ORIGIN.y);
    this.root.addChild(sprite);

    const from = { ...PLAYER_ORIGIN };
    tween({
      durationMs: 300,
      easing: Easings.linear,
      onUpdate: (t) => {
        sprite.position.set(
          from.x + (target.x - from.x) * t,
          from.y + (target.y - from.y) * t,
        );
      },
    });

    this.active.push({ bet, sprite });
  }

  /**
   * Animate by bet result. `winningBets` is the set of bet types that hit;
   * everything else flies to the dealer. Auto-destroys after 600 ms.
   */
  animateWinningChips(winningBets: ReadonlySet<BetType>): void {
    const oldActive = this.active;
    this.active = [];
    oldActive.forEach((c, i) => {
      const target = winningBets.has(c.bet) ? PLAYER_WIN_TARGET : DEALER_TARGET;
      this.flyAndFade(c, target, i);
    });
    this.scheduleDestroyAll(oldActive, 600);
  }

  animateLosingChips(): void {
    const oldActive = this.active;
    this.active = [];
    oldActive.forEach((c, i) => this.flyAndFade(c, DEALER_TARGET, i));
    this.scheduleDestroyAll(oldActive, 600);
  }

  /**
   * Half-half (push) animation. First half of chips → dealer, second
   * half → player. Mirrors moveHalfHalf (baccarat_flame_game equivalent).
   */
  animateTie(): void {
    const oldActive = this.active;
    this.active = [];
    const half = Math.ceil(oldActive.length / 2);
    oldActive.forEach((c, i) => {
      const target = i < half ? DEALER_TARGET : PLAYER_WIN_TARGET;
      this.flyAndFade(c, target, i);
    });
    this.scheduleDestroyAll(oldActive, 600);
  }

  hardClear(): void {
    for (const c of this.active) c.sprite.destroy();
    this.active = [];
  }

  hasAnyChips(): boolean {
    return this.active.length > 0;
  }

  private flyAndFade(c: ActiveChip, target: XY, index: number): void {
    const fromX = c.sprite.position.x;
    const fromY = c.sprite.position.y;
    const fromAlpha = c.sprite.alpha;
    const delayMs = 20 * index;

    window.setTimeout(() => {
      tween({
        durationMs: 500,
        easing: Easings.linear,
        onUpdate: (t) => {
          c.sprite.position.set(
            fromX + (target.x - fromX) * t,
            fromY + (target.y - fromY) * t,
          );
          c.sprite.alpha = fromAlpha + (0.2 - fromAlpha) * t;
        },
      });
    }, delayMs);
  }

  private scheduleDestroyAll(list: ActiveChip[], delayMs: number): void {
    window.setTimeout(() => {
      for (const c of list) {
        try {
          c.sprite.destroy();
        } catch {
          /* already destroyed */
        }
      }
    }, delayMs);
  }
}
